import { EditorTree, EditorNode, OrganizationDirection, OrganizationMode } from '../types/types';

/**
 * Tree Utils are utility functions for managing the current tree at an internal level
 * 
 * This service is focused on:
 * 1. Generating unique node IDs
 * 2. Getting node levels based on parent
 * 3. Organizing tree layout based on direction
 * 4. Positioning nodes based on their levels
 * 5. Organizing tree layout based on direction and dimensions
 * 
 */


// Tree utility functions
class TreeUtilsClass {

   /**
   * Generate a hierarchical node ID using Dewey Decimal-style notation (1, 1.1, 1.1.1, etc.)
   * Uses the parent's children array to find siblings efficiently
   */
  generateNodeId(tree: EditorTree | null, parentId?: string): string {
    if (!tree) return '1'; // First root node

    if (!parentId || parentId === undefined) {
      let nextId = 1;
      if (tree && tree.nodes) {
        const rootIds = tree.nodes
          .filter(n => !n.parent_id)
          .map(n => parseInt(n.id, 10))
          .filter(n => !isNaN(n));
        while (rootIds.includes(nextId)) {
          nextId++;
        }
      }
      return nextId.toString();
    }
    
    // Find the parent node
    const parentNode = tree.nodes.find(n => n.id === parentId);
    if (!parentNode) return `${parentId}.1`; // Fallback if parent not found
    
    // Find siblings from the parent's children array
    const siblingIds: number[] = [];
    parentNode.children.forEach(childId => {
      const child = tree.nodes.find(n => n.id === childId);
      if (child && child.id.startsWith(`${parentId}.`)) {
        const parts = child.id.split('.');
        const lastPart = parseInt(parts[parts.length - 1], 10);
        if (!isNaN(lastPart)) {
          siblingIds.push(lastPart);
        }
      }
    });
    
    // Sort the sibling IDs numerically
    siblingIds.sort((a, b) => a - b);
    
    // Get the next number in sequence
    const nextNum = siblingIds.length ? siblingIds[siblingIds.length - 1] + 1 : 1;
    return `${parentId}.${nextNum}`;
  }

  // Get node level based on its parent
  getNodeLevel(tree: EditorTree | null, parentId?: string): number {
    if (!parentId || !tree) return 0;
    
    const parentNode = tree.nodes.find(n => n.id === parentId);
    if (!parentNode) return 0;
    
    return (parentNode.level ?? 0) + 1;
  }

  // Organize tree layout
  organizeTreeLayout(tree: EditorTree, direction: OrganizationDirection, organizationType: OrganizationMode, dimensions: { width: number, height: number }): EditorTree {
    if (!tree.nodes.length) return tree;
    if (organizationType === 'manual') return tree; // No need to organize if manual
    console.log("Organization mode:", organizationType);

    const rootNodes = tree.nodes.filter(node => !node.parent_id);
    if (rootNodes.length === 0) return tree;

    // Build a node map for quick lookups
    const nodeMap = new Map<string, EditorNode>();
    tree.nodes.forEach(node => nodeMap.set(node.id, node));

    // Collect nodes by level for sorting
    const nodesByLevel = new Map<number, EditorNode[]>();
    
    // For cluster mode, we need to identify terminal nodes
    const terminalNodes = organizationType === 'cluster' 
      ? tree.nodes.filter(node => node.type === 'terminal')
      : [];

    // First pass: Organize nodes into levels and collect preliminary data
    const organizeSubtree = (
      node: EditorNode, 
      level: number, 
      _indexInLevel: number,
      levelTotals: Map<number, number>
    ) => {
      // For cluster mode, terminal nodes should be at the maximum level
      const nodeLevel = organizationType === 'cluster' && node.type === 'terminal'
        ? -1 // Temporary marker for terminal nodes in cluster mode
        : level;
      
      // Store level in node
      node.level = nodeLevel;
      
      // For regular tree mode or non-terminal nodes in cluster mode
      if (nodeLevel >= 0) {
        // Update count of nodes at this level
        const nodeCountAtLevel = levelTotals.get(nodeLevel) || 0;
        levelTotals.set(nodeLevel, nodeCountAtLevel + 1);
        
        // Add node to its level collection
        const nodesAtLevel = nodesByLevel.get(nodeLevel) || [];
        nodesAtLevel.push(node);
        nodesByLevel.set(nodeLevel, nodesAtLevel);
      }
      
      // Find children
      const children = tree.nodes.filter(n => n.parent_id === node.id);
      children.forEach((child, i) => {
        organizeSubtree(child, level + 1, i, levelTotals);
      });
    };

    // Execute organization for each root node
    const levelTotals = new Map<number, number>();
    rootNodes.forEach((root, i) => {
      organizeSubtree(root, 0, i, levelTotals);
    });
    
    // Handle terminal nodes for cluster mode
    if (organizationType === 'cluster' && terminalNodes.length > 0) {
      // Find the maximum level across all nodes (not just the ones we've processed)
      // This ensures terminal nodes are always at the bottom
      const maxLevel = Math.max(
        ...tree.nodes
          .filter(node => node.type !== 'terminal')
          .map(node => node.level || 0)
      );
      
      // Set terminal nodes to be one level below the deepest non-terminal node
      const terminalLevel = maxLevel + 1;
      
      // Update terminal nodes' level
      terminalNodes.forEach(node => {
        node.level = terminalLevel;
      });
      
      // Add terminal nodes to their level collection
      const terminalNodesAtLevel = nodesByLevel.get(terminalLevel) || [];
      terminalNodes.forEach(node => terminalNodesAtLevel.push(node));
      nodesByLevel.set(terminalLevel, terminalNodesAtLevel);
      
      // Update level totals
      levelTotals.set(terminalLevel, terminalNodes.length);
    }
    
    // Sort nodes at each level by ID to prevent crossing
    nodesByLevel.forEach((nodes, level) => {
      nodesByLevel.set(level, nodes.sort((a, b) => {
        // Sort numerically by ID parts
        const aParts = a.id.split('.').map(Number);
        const bParts = b.id.split('.').map(Number);
        
        for (let i = 0; i < Math.min(aParts.length, bParts.length); i++) {
          if (aParts[i] !== bParts[i]) {
            return aParts[i] - bParts[i];
          }
        }
        return aParts.length - bParts.length;
      }));
    });
    
    // Calculate spacing based on text content
    let minNodeWidth = 40;
    let minNodeHeight = 40; 
    const fontSize = 16;

    // First pass: determine maximum text sizes
    let maxNameLength = 0;
    let maxValueLength = 0;
    let maxEdgeNameLength = 0;
    let maxEdgeProbLength = 0;
    let maxEdgePayoffLength = 0;

    tree.nodes.forEach(node => {
      // Track maximum text lengths for consistent spacing
      maxNameLength = Math.max(maxNameLength, node.name.length);
      maxValueLength = Math.max(maxValueLength, node.value?.toString().length || 0);

      maxEdgeNameLength = Math.max(maxEdgeNameLength, node.edge.name?.length || 0);
      maxEdgeProbLength = Math.max(maxEdgeProbLength, node.edge.probability ? (node.edge.probability * 100).toFixed(0).length + 1 : 0); // +1 for % sign
      maxEdgePayoffLength = Math.max(maxEdgePayoffLength, node.edge.payoff?.toString().length || 0);
    });

    // Calculate widths based on text content
    const charWidth = 8; // Average character width in pixels
    const nodeTextWidth = Math.max(maxNameLength, maxValueLength) * charWidth;
    const edgeTextWidth = Math.max(maxEdgeNameLength, maxEdgeProbLength, maxEdgePayoffLength) * charWidth;
    
    // Set minimum widths based on content
    minNodeWidth = Math.max(minNodeWidth, nodeTextWidth); // Add 50% for padding
    minNodeHeight = Math.max(minNodeHeight, fontSize * 5); // Space for multiple text lines
    
    // Add substantial padding between nodes to prevent any text overlap
    const NODE_HORIZONTAL_PADDING = Math.max(30, edgeTextWidth); // Ensure space for edge labels
    const NODE_VERTICAL_PADDING = Math.max(40, minNodeHeight); // Ensure vertical space between levels

    // Calculate final spacing values
    const horizontalSpacing = minNodeWidth + NODE_HORIZONTAL_PADDING;
    const verticalSpacing = minNodeHeight + NODE_VERTICAL_PADDING;
    
    // NEW APPROACH: Bottom-up positioning to ensure parents are centered above their children
    if (direction === OrganizationDirection.VERTICAL) {
      // First pass: Position all leaf nodes
      // Find the maximum level (deepest in the tree)
      const maxLevel = Math.max(...Array.from(nodesByLevel.keys()));
      
      // Start with the leaf nodes and work our way up
      // Create a temporary map to store node positions
      const preliminaryPositions = new Map<string, { x: number, y: number }>();
      
      // Position leaf nodes first (nodes at the maximum level)
      let xOffset = 0;
      const leafNodes = nodesByLevel.get(maxLevel) || [];
      leafNodes.forEach(node => {
        preliminaryPositions.set(node.id, { 
          x: xOffset + horizontalSpacing/2, 
          y: (maxLevel + 1) * verticalSpacing 
        });
        xOffset += horizontalSpacing;
      });
      
      // Work backwards from the deepest level to the root
      for (let level = maxLevel - 1; level >= 0; level--) {
        const nodesAtLevel = nodesByLevel.get(level) || [];
        
        nodesAtLevel.forEach(node => {
          // Find all children of this node
          const childPositions = node.children
            .map(childId => preliminaryPositions.get(childId))
            .filter(pos => pos !== undefined) as { x: number, y: number }[];
          
          if (childPositions.length > 0) {
            // Position the node centered above its children
            const minChildX = Math.min(...childPositions.map(p => p.x));
            const maxChildX = Math.max(...childPositions.map(p => p.x));
            const centerX = (minChildX + maxChildX) / 2;
            
            preliminaryPositions.set(node.id, {
              x: centerX,
              y: (level + 1) * verticalSpacing
            });
          } else {
            // Handle nodes with no positioned children
            preliminaryPositions.set(node.id, {
              x: xOffset + horizontalSpacing/2,
              y: (level + 1) * verticalSpacing
            });
            xOffset += horizontalSpacing;
          }
        });
      }
      
      // Adjust positions to ensure minimum horizontal spacing and center the entire tree
      const allXPositions = Array.from(preliminaryPositions.values()).map(p => p.x);
      const minX = Math.min(...allXPositions);
      const maxX = Math.max(...allXPositions);
      const treeWidth = maxX - minX + horizontalSpacing;
      
      // Center the tree horizontally on the canvas
      const horizontalOffset = Math.max(0, (dimensions.width - treeWidth) / 2);
      
      // Apply calculated positions to the actual nodes
      tree.nodes.forEach(node => {
        const position = preliminaryPositions.get(node.id);
        if (position) {
          node.x = position.x - minX + horizontalOffset;
          node.y = position.y;
        }
      });
    } else { // HORIZONTAL LAYOUT
      // Position nodes in a similar way but for horizontal layout
      // Find the maximum level
      const maxLevel = Math.max(...Array.from(nodesByLevel.keys()));
      
      // Create a temporary map to store node positions
      const preliminaryPositions = new Map<string, { x: number, y: number }>();
      
      // Position leaf nodes first (nodes at the maximum level)
      let yOffset = 0;
      const leafNodes = nodesByLevel.get(maxLevel) || [];
      leafNodes.forEach(node => {
        preliminaryPositions.set(node.id, { 
          x: (maxLevel + 1) * horizontalSpacing,
          y: yOffset + verticalSpacing/2
        });
        yOffset += verticalSpacing;
      });
      
      // Work backwards from the deepest level to the root
      for (let level = maxLevel - 1; level >= 0; level--) {
        const nodesAtLevel = nodesByLevel.get(level) || [];
        
        nodesAtLevel.forEach(node => {
          // Find all children of this node
          const childPositions = node.children
            .map(childId => preliminaryPositions.get(childId))
            .filter(pos => pos !== undefined) as { x: number, y: number }[];
          
          if (childPositions.length > 0) {
            // Position the node centered to the left of its children
            const minChildY = Math.min(...childPositions.map(p => p.y));
            const maxChildY = Math.max(...childPositions.map(p => p.y));
            const centerY = (minChildY + maxChildY) / 2;
            
            preliminaryPositions.set(node.id, {
              x: (level + 1) * horizontalSpacing,
              y: centerY
            });
          } else {
            // Handle nodes with no positioned children
            preliminaryPositions.set(node.id, {
              x: (level + 1) * horizontalSpacing,
              y: yOffset + verticalSpacing/2
            });
            yOffset += verticalSpacing;
          }
        });
      }
      
      // Adjust positions to ensure minimum vertical spacing and center the entire tree
      const allYPositions = Array.from(preliminaryPositions.values()).map(p => p.y);
      const minY = Math.min(...allYPositions);
      const maxY = Math.max(...allYPositions);
      const treeHeight = maxY - minY + verticalSpacing;
      
      // Center the tree vertically on the canvas
      const verticalOffset = Math.max(0, (dimensions.height - treeHeight) / 2);
      
      // Apply calculated positions to the actual nodes
      tree.nodes.forEach(node => {
        const position = preliminaryPositions.get(node.id);
        if (position) {
          node.x = position.x;
          node.y = position.y - minY + verticalOffset;
        }
      });
    }
    
    return tree;
  }

  // mark node and all children recursively as optimal
  markSubTreeOptimal(tree: EditorTree | null, startNode: EditorNode, optimal_choice: boolean): void {
    if (!tree || !startNode) return;

    startNode.optimal_choice = optimal_choice;
    startNode.children.forEach(childId => {
      const child = tree.nodes.find(n => n.id === childId);
      // only decisions mark trees as optimal so if a decision is in a subtree it should handle it's children
      if (child && child.type !== 'decision') this.markSubTreeOptimal(tree, child, optimal_choice);
    });
  }

}

export const treeUtils = new TreeUtilsClass();